import { useCallback, useEffect, useRef, useState } from 'react';
import { streamTrafficInvalidations } from '../services/adminApi';
import { AdminTrafficInvalidation } from '../types';

type LiveState = 'live' | 'reconnecting' | 'stale';

interface Options {
  refresh: () => Promise<boolean>;
  shouldRefresh: (event: AdminTrafficInvalidation) => boolean;
  onAuthError: (error: any) => boolean;
}

export const useAdminTrafficLive = ({
  refresh,
  shouldRefresh,
  onAuthError
}: Options) => {
  const [state, setState] = useState<LiveState>('reconnecting');
  const [lastSyncedAt, setLastSyncedAt] = useState<Date | null>(null);
  const refreshRef = useRef(refresh);
  const filterRef = useRef(shouldRefresh);
  const authErrorRef = useRef(onAuthError);

  useEffect(() => { refreshRef.current = refresh; }, [refresh]);
  useEffect(() => { filterRef.current = shouldRefresh; }, [shouldRefresh]);
  useEffect(() => { authErrorRef.current = onAuthError; }, [onAuthError]);

  const markSynced = useCallback(() => {
    setLastSyncedAt(new Date());
  }, []);

  useEffect(() => {
    let disposed = false;
    let stoppedForAuth = false;
    let connectedOnce = false;
    let isConnected = false;
    let reconnectDelay = 1000;
    let streamController: AbortController | null = null;
    let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
    let pollTimer: ReturnType<typeof setInterval> | null = null;
    let staleTimer: ReturnType<typeof setTimeout> | null = null;
    let debounceTimer: ReturnType<typeof setTimeout> | null = null;

    const clearTimer = (timer: ReturnType<typeof setTimeout> | null) => {
      if (timer) clearTimeout(timer);
    };
    const armStaleTimer = () => {
      clearTimer(staleTimer);
      staleTimer = setTimeout(() => {
        if (!disposed && !stoppedForAuth && !isConnected) setState('stale');
      }, 30000);
    };
    const refreshData = async () => {
      const success = await refreshRef.current();
      if (success && !disposed) {
        setLastSyncedAt(new Date());
      }
      return success;
    };
    const startFallback = () => {
      if (pollTimer || disposed || stoppedForAuth) return;
      armStaleTimer();
      pollTimer = setInterval(() => {
        void refreshData();
      }, 15000);
    };
    const stopFallback = () => {
      if (pollTimer) clearInterval(pollTimer);
      pollTimer = null;
      clearTimer(staleTimer);
      staleTimer = null;
    };
    const scheduleReconnect = () => {
      if (disposed || stoppedForAuth || reconnectTimer) return;
      isConnected = false;
      setState(previous => previous === 'stale' ? previous : 'reconnecting');
      startFallback();
      reconnectTimer = setTimeout(() => {
        reconnectTimer = null;
        void connect();
      }, reconnectDelay);
      reconnectDelay = Math.min(reconnectDelay * 2, 15000);
    };
    const connect = async () => {
      if (disposed || stoppedForAuth) return;
      streamController?.abort();
      streamController = new AbortController();
      try {
        await streamTrafficInvalidations(
          streamController.signal,
          () => {
            if (disposed) return;
            const isReconnect = connectedOnce || reconnectDelay > 1000;
            connectedOnce = true;
            isConnected = true;
            reconnectDelay = 1000;
            stopFallback();
            setState('live');
            if (isReconnect) void refreshData();
          },
          event => {
            if (!filterRef.current(event) || debounceTimer) return;
            debounceTimer = setTimeout(() => {
              debounceTimer = null;
              void refreshData();
            }, 1000);
          }
        );
        if (!disposed && !streamController.signal.aborted) scheduleReconnect();
      } catch (error: any) {
        if (disposed || streamController.signal.aborted) return;
        if (error?.status === 401 || error?.status === 403) {
          stoppedForAuth = true;
          stopFallback();
          authErrorRef.current({ response: { status: error.status } });
          return;
        }
        scheduleReconnect();
      }
    };

    void connect();
    return () => {
      disposed = true;
      streamController?.abort();
      clearTimer(reconnectTimer);
      clearTimer(debounceTimer);
      clearTimer(staleTimer);
      if (pollTimer) clearInterval(pollTimer);
    };
    // The long-lived connection reads changing callbacks through refs so a
    // render cannot tear down and recreate the authenticated stream.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { state, lastSyncedAt, markSynced };
};
